import { ReactSVGPanZoom, TOOL_AUTO } from "react-svg-pan-zoom";
import React, {
  useState,
  useRef,
  useEffect,
  useCallback,
  useReducer,
} from "react";
import { ReactSvgPanZoomLoader } from "../utils/ReactSvgPanZoomLoader.js";
import MapEffectComponent from "./MapEffectComponent.js";
import Button from "@material-ui/core/Button";
import MapDialog from "./dialog/MapDialog";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import CircularProgress from "@material-ui/core/CircularProgress";
import campusData from "../../json/campus_data.json";
import { USE_GPS } from "../../config/gpsConfig.js";
import { loadMapData } from "../utils/map/loadMapData.js";
import MapToolbar from "./MapToolbar.jsx";
import SearchPin from "./marker/SearchPin.jsx";
import GpsMarker from "./marker/GpsMarker.jsx";
import NearestCampusSnackbar from "./snackbar/NearestCampusSnackbar.jsx";
import IconGuideSnackbar from "./snackbar/IconGuideSnackbar.jsx";
import { createSearchQueryController } from "../utils/searchQueryController.js";
import {
  initGpsOnMount,
  cleanupGps,
  initGpsOnSvgReady,
  centerOnCurrentGps,
  createGpsStore,
} from "./managers/gpsManager.js";
import {
  ensureIndoorLayerContainer,
  stashFloors,
  changeFloor,
  createFloorStore,
} from "./managers/floorManager.js";
import { handleSelect, handleClick } from "./managers/selectionHandler.js";
import {
  handleCampusChange,
  initializeMap,
  clearSearchPin,
} from "./managers/campusManager.js";
import {
  searchPinReducer,
  searchPinInitialState,
  gpsReducer,
  gpsInitialState,
  mapReducer,
  createMapInitialState,
  uiReducer,
  uiInitialState,
  gpsActions,
  mapActions,
  uiActions,
  MapModeEnum,
} from "./reducers/mapPanZoomReducer.js";

export default function MapPanZoomWrapper(props) {
  const { width, height, initialCampus, gpsEnabled, updateGpsEnabled } = props;

  // Reducers
  const [searchPin, searchPinDispatch] = useReducer(
    searchPinReducer,
    searchPinInitialState,
  );
  const [gpsState, gpsDispatch] = useReducer(gpsReducer, gpsInitialState);
  const [mapState, mapDispatch] = useReducer(
    mapReducer,
    initialCampus,
    createMapInitialState,
  );
  const [uiState, uiDispatch] = useReducer(uiReducer, uiInitialState);

  // Local state
  const [tool, setTool] = useState(TOOL_AUTO);
  const [value, setValue] = useState({});
  const [mapData, setMapData] = useState({
    mapJson: [],
    mapIconJson: [],
    mapModelJson: [],
    mapSvg: [],
  });

  // Refs
  const viewerRef = useRef(null);
  const svgRootRef = useRef(null);
  const gpsStoreRef = useRef(createGpsStore(mapState.currentCampus));
  const floorStoreRef = useRef(createFloorStore());
  const searchQueryControllerRef = useRef(null);
  const gpsAccuracyRef = useRef(null);

  // searchQueryController 内のコールバックが古い state を保持しないよう、最新 state を ref 経由で参照させるため
  const searchQueryStateRef = useRef({
    isReady: mapState.isReady,
    currentCampus: mapState.currentCampus,
    currentSearchArrayLength: mapState.currentSearchArray.length,
    currentBuildingArray: mapState.currentBuildingArray,
    currentMapJson: mapState.currentMapJson,
    currentFloorArray: mapState.currentFloorArray,
    currentRoomArray: mapState.currentRoomArray,
    currentMode: mapState.currentMode,
    mapIconJson: mapData.mapIconJson,
    searchPin,
  });
  searchQueryStateRef.current = {
    isReady: mapState.isReady,
    currentCampus: mapState.currentCampus,
    currentSearchArrayLength: mapState.currentSearchArray.length,
    currentBuildingArray: mapState.currentBuildingArray,
    currentMapJson: mapState.currentMapJson,
    currentFloorArray: mapState.currentFloorArray,
    currentRoomArray: mapState.currentRoomArray,
    currentMode: mapState.currentMode,
    mapIconJson: mapData.mapIconJson,
    searchPin,
  };

  gpsAccuracyRef.current = gpsState.coords?.accuracy ?? null;

  // コンポーネント内とmanager群で利用する統合dispatch（domainで各reducerに振り分け）
  const dispatch = useCallback(
    (action) => {
      switch (action.domain) {
        case "searchPin":
          searchPinDispatch(action);
          break;
        case "gps":
          gpsDispatch(action);
          break;
        case "map":
          mapDispatch(action);
          break;
        case "ui":
          uiDispatch(action);
          break;
        default:
          console.warn("Unknown action domain:", action.domain);
      }
    },
    [searchPinDispatch, gpsDispatch, mapDispatch, uiDispatch],
  );

  const updateSearchParamsFromSelection = useCallback((selection) => {
    searchQueryControllerRef.current?.updateSearchParamsFromSelection(
      selection,
    );
  }, []);

  // mapState.currentCampus を GPS/コンパス購読用 ref に同期
  useEffect(() => {
    gpsStoreRef.current.runtimeCampus = mapState.currentCampus;
  }, [mapState.currentCampus]);

  // ── searchQueryControllerの初期化 ──
  useEffect(() => {
    searchQueryControllerRef.current = createSearchQueryController({
      clearSearchPin: () => clearSearchPin(dispatch),
      handleCampusChange: (v) => {
        handleCampusChange(
          viewerRef,
          dispatch,
          updateSearchParamsFromSelection,
          v,
        );
      },
      handleSelect: (id) => {
        const mapSlice = {
          currentBuildingArray:
            searchQueryStateRef.current.currentBuildingArray,
          currentMapJson: searchQueryStateRef.current.currentMapJson,
          currentFloorArray: searchQueryStateRef.current.currentFloorArray,
          currentRoomArray: searchQueryStateRef.current.currentRoomArray,
          currentCampus: searchQueryStateRef.current.currentCampus,
          currentMode: searchQueryStateRef.current.currentMode,
        };

        handleSelect(
          floorStoreRef,
          svgRootRef,
          viewerRef,
          dispatch,
          mapSlice,
          searchQueryStateRef.current.mapIconJson,
          searchQueryStateRef.current.searchPin,
          id,
        );
      },
      getIsReady: () => searchQueryStateRef.current.isReady,
      getSvgRoot: () => svgRootRef.current,
      getCurrentCampus: () => searchQueryStateRef.current.currentCampus,
      getSearchArrayLength: () =>
        searchQueryStateRef.current.currentSearchArrayLength,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── isReadyがtrueになったらフロア初期化 ──
  useEffect(() => {
    if (mapState.isReady && svgRootRef.current) {
      ensureIndoorLayerContainer(svgRootRef.current);
      stashFloors(
        floorStoreRef.current,
        mapState.currentCampus,
        mapData.mapJson,
      );
      initGpsOnSvgReady(
        gpsStoreRef,
        svgRootRef,
        dispatch,
        mapState.currentCampus,
        gpsAccuracyRef.current,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapState.isReady,
    mapState.currentCampus,
    mapData.mapJson,
  ]);

  // ── マウント時のGPS初期化 ──
  useEffect(() => {
    const gpsStore = gpsStoreRef.current;

    if (viewerRef.current) viewerRef.current.fitToViewer();

    initGpsOnMount(gpsStoreRef, svgRootRef, dispatch, {
      gpsEnabled,
      updateGpsEnabled,
      initialCampus,
    });

    return () => {
      cleanupGps(gpsStore);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialCampus, gpsEnabled, updateGpsEnabled]);

  // ── IconGuide Snackbar の表示 ──
  useEffect(() => {
    const showIconGuide =
      process.env.REACT_APP_SHOW_ICON_GUIDE_SNACKBAR === "true";
    if (showIconGuide) {
      dispatch(uiActions.setIconGuideSnackbar(true));
    }
  }, [dispatch]);

  // ── isReady・GPS可視状態変更時の処理 ──
  useEffect(() => {
    searchQueryControllerRef.current?.queueSearchFromQuery();
    searchQueryControllerRef.current?.tryHandlePendingSearchSelection();

    if (
      gpsStoreRef.current.pendingCenterOnGps &&
      mapState.isReady &&
      gpsState.markerVisible
    ) {
      gpsStoreRef.current.pendingCenterOnGps = false;
      const gpsSlice = {
        nearestCampus: gpsState.nearestCampus,
        markerVisible: gpsState.markerVisible,
        markerX: gpsState.markerX,
        markerY: gpsState.markerY,
      };
      const mapSlice = {
        currentCampus: mapState.currentCampus,
        currentCampusCode: mapState.currentCampusCode,
      };

      centerOnCurrentGps(
        gpsStoreRef,
        viewerRef,
        svgRootRef,
        gpsSlice,
        mapSlice,
        {
          gpsEnabled,
          handleCampusChange: (v) => {
            handleCampusChange(
              viewerRef,
              dispatch,
              updateSearchParamsFromSelection,
              v,
            );
          },
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    mapState.isReady,
    gpsState.markerVisible,
    gpsState.nearestCampus,
    gpsState.markerX,
    gpsState.markerY,
    mapState.currentCampus,
    mapState.currentCampusCode,
    gpsEnabled,
  ]);

  // ── マップデータのロード ──
  useEffect(() => {
    if (mapData.mapSvg.length === 0) {
      loadMapData().then((data) => {
        setMapData({
          mapSvg: data.mapSvg,
          mapJson: data.mapJson,
          mapIconJson: data.mapIconJson,
          mapModelJson: data.mapModelJson,
        });
      });
    }
  }, [mapData.mapSvg.length]);

  // ── キャンパス変更 ──
  const handleCampusChangeCallback = useCallback((campusValue) => {
    handleCampusChange(
      viewerRef,
      dispatch,
      updateSearchParamsFromSelection,
      campusValue,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── クリックハンドラ ──
  const handleClickCallback = useCallback(
    (target_id) => {
      const mapSlice = {
        currentBuildingArray: mapState.currentBuildingArray,
        currentMapJson: mapState.currentMapJson,
        currentCampus: mapState.currentCampus,
      };

      handleClick(
        floorStoreRef,
        svgRootRef,
        dispatch,
        mapSlice,
        mapData.mapIconJson,
        searchPin,
        target_id,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      mapState.currentBuildingArray,
      mapState.currentMapJson,
      mapState.currentCampus,
      mapData.mapIconJson,
      searchPin,
    ],
  );

  // ── マップ初期化 ──
  const handleInitializeMap = useCallback(() => {
    initializeMap(
      viewerRef,
      svgRootRef,
      floorStoreRef,
      dispatch,
      mapState.currentCampus,
      mapData.mapJson,
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapState.currentCampus, mapData.mapJson]);

  // ── 視点リセット（パン/ズームのみ） ──
  const handleResetView = useCallback(() => {
    viewerRef.current?.reset?.();
  }, []);

  // ── フロア変更 ──
  const handleChangeFloor = useCallback(
    (index) => {
      const mapSlice = {
        currentCampus: mapState.currentCampus,
        currentSelectedFloors: mapState.currentSelectedFloors,
      };

      changeFloor(
        floorStoreRef,
        svgRootRef,
        dispatch,
        mapSlice,
        searchPin,
        index,
      );
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mapState.currentCampus, mapState.currentSelectedFloors, searchPin],
  );

  // ── GPS中央フォーカス ──
  const handleCenterOnCurrentGps = useCallback(() => {
    const gpsSlice = {
      nearestCampus: gpsState.nearestCampus,
      markerVisible: gpsState.markerVisible,
      markerX: gpsState.markerX,
      markerY: gpsState.markerY,
    };
    const mapSlice = {
      currentCampus: mapState.currentCampus,
      currentCampusCode: mapState.currentCampusCode,
    };

    centerOnCurrentGps(gpsStoreRef, viewerRef, svgRootRef, gpsSlice, mapSlice, {
      gpsEnabled,
      handleCampusChange: (v) => {
        handleCampusChange(
          viewerRef,
          dispatch,
          updateSearchParamsFromSelection,
          v,
        );
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    gpsEnabled,
    gpsState.nearestCampus,
    gpsState.markerVisible,
    gpsState.markerX,
    gpsState.markerY,
    mapState.currentCampus,
    mapState.currentCampusCode,
  ]);

  // ── Snackbar ──
  const handleCloseNearestCampusSnackbar = useCallback(() => {
    dispatch(gpsActions.dismissSnackbar());
  }, [dispatch]);

  const handleCloseIconGuideSnackbar = useCallback(() => {
    dispatch(uiActions.setIconGuideSnackbar(false));
  }, [dispatch]);

  const handleConfirmNearestCampus = useCallback(() => {
    if (
      gpsState.nearestCampus &&
      gpsState.nearestCampus !== mapState.currentCampus
    ) {
      handleCampusChange(
        viewerRef,
        dispatch,
        updateSearchParamsFromSelection,
        gpsState.nearestCampus,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gpsState.nearestCampus, mapState.currentCampus]);

  // ── 検索 ──
  const handleSearchSelection = useCallback((val, reason) => {
    searchQueryControllerRef.current?.handleSearchSelection(val, reason);
  }, []);

  // ── SVG Ready ──
  const handleSvgReady = useCallback(
    (svgRoot) => {
      if (!svgRoot) return;
      svgRootRef.current = svgRoot;

      gpsStoreRef.current.transformModels[mapState.currentCampus] =
        mapData.mapModelJson[mapState.currentCampus - 1];
      gpsStoreRef.current.prevSvgPos[mapState.currentCampus] = null;

      dispatch(mapActions.setReady(true));
    },
    [dispatch, mapState.currentCampus, mapData.mapModelJson],
  );

  const indoorChanger =
    mapState.currentMode === MapModeEnum.INDOOR ? (
      <ButtonGroup
        orientation="vertical"
        color="secondary"
        size="small"
        aria-label="vertical contained primary button group "
        variant="contained"
      >
        {mapState.currentSelectedFloors.map((floorValue, index) => (
          <Button
            color={
              mapState.currentSelectedFloorIndex === index ? "primary" : null
            }
            onClick={() => handleChangeFloor(index)}
            key={index}
          >
            {floorValue.floor_name}
          </Button>
        ))}
      </ButtonGroup>
    ) : null;

  if (mapData.mapSvg.length === 0) {
    return (
      <div
        style={{
          width: "100%",
          height: "100%",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          backgroundColor: "#F4F4F4",
        }}
      >
        <CircularProgress />
      </div>
    );
  }

  return (
    <>
      <MapDialog
        onClose={() => dispatch(uiActions.closeDialog())}
        open={uiState.dialogIsOpen}
        marker={uiState.selectedMarker}
      />
      <MapEffectComponent
        isReady={mapState.isReady}
        initializeMap={handleInitializeMap}
      />
      <ReactSvgPanZoomLoader
        key={`campus-${mapState.currentCampus}`}
        src={mapData.mapSvg[mapState.currentCampus - 1]}
        onSVGReady={handleSvgReady}
        render={(content) => (
          <ReactSVGPanZoom
            background={"#F4F4F4"}
            SVGBackground={"#F4F4F4"}
            ref={(Viewer) => (viewerRef.current = Viewer)}
            width={width}
            height={height}
            tool={tool}
            onChangeTool={setTool}
            value={value}
            toolbarProps={{ position: "none" }}
            miniatureProps={{ position: "none" }}
            scaleFactor={1.5}
            scaleFactorOnWheel={1.2}
            scaleFactorMin={1.0}
            preventPanOutside={true}
            detectAutoPan={false}
            disableDoubleClickZoomWithToolAuto={false}
            onClick={(viewerEvent) =>
              handleClickCallback(viewerEvent.originalEvent.target.id)
            }
            onTouchStart={(viewerEvent) =>
              handleClickCallback(viewerEvent.originalEvent.target.id)
            }
            onChangeValue={setValue}
          >
            <svg width={width} height={height}>
              {content}
            </svg>
          </ReactSVGPanZoom>
        )}
      />
      <MapToolbar
        handleClick={handleSearchSelection}
        handleCenterOnGps={handleCenterOnCurrentGps}
        handleResetView={handleResetView}
        useGps={USE_GPS}
        isGpsAvailable={gpsEnabled !== "denied" && gpsEnabled !== "retry"}
        currentCampus={mapState.currentCampus}
        changeCampus={handleCampusChangeCallback}
        listArray={mapState.currentSearchArray}
      >
        {indoorChanger}
      </MapToolbar>
      <SearchPin
        svgRoot={svgRootRef.current}
        x={searchPin.x}
        y={searchPin.y}
        visible={searchPin.visible}
        zoom={value?.a ?? 1}
        scale={
          campusData.campusList[mapState.currentCampus - 1].map?.searchPin
            ?.scale ?? 1
        }
      />
      <GpsMarker
        svgRoot={svgRootRef.current}
        x={gpsState.markerX}
        y={gpsState.markerY}
        visible={gpsState.markerVisible}
        zoom={value?.a ?? 1}
        accuracy={gpsState.coords?.accuracy}
        accuracyRadius={gpsState.accuracyRadius}
        headingDeg={gpsState.headingDeg}
        scale={
          campusData.campusList[mapState.currentCampus - 1].map?.gpsMarker
            ?.scale ?? 1
        }
      />
      <NearestCampusSnackbar
        open={gpsState.showNearestCampusSnackbar}
        onClose={handleCloseNearestCampusSnackbar}
        onConfirm={handleConfirmNearestCampus}
        nearestCampus={gpsState.nearestCampus}
      />
      <IconGuideSnackbar
        open={uiState.showIconGuideSnackbar}
        onClose={handleCloseIconGuideSnackbar}
      />
    </>
  );
}
